import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import Downshift from 'downshift';
import { questionCircle } from 'lib/icons';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import useOperatorLogout from 'components/LaborTicket/hooks/useOperatorLogout';
import Routes from 'lib/utils/routes';

import { getCurrentOperator } from 'lib/selectors/getCurrentOperator';
import { getActiveOperators } from 'lib/selectors/getActiveOperators';

import { SignInModalWrapper } from 'components/SignInModal';
import { SignOutModal } from 'components/SignOutModal';
import { SearchInput } from 'components/SearchInput';
import { EmptyWithGraphic } from 'components/Empty/EmptyWithGraphic';
import NoOperators from 'graphics/NoOperators';
import _startCase from 'lodash/startCase';

import { getOperatorsLoadedAt } from 'lib/selectors/getOperators';
import { getPinPadSignInEnabled } from 'lib/selectors/getPinPadSignInEnabled';
import {
  findOperatorByEmployeeId,
  EMPLOYEE_ID_MATCH,
} from 'lib/utils/findOperatorByEmployeeId';
import { getIsJobLaborTrackingEnabled } from 'lib/selectors/getIsJobLaborTrackingEnabled';
import { getIsLaborTicketEnabled } from 'lib/selectors/getIsLaborTicketEnabled';
import { useIsLaborTicketFlow } from 'lib/hooks/useIsLaborTicketFlow';
import { getMachine } from 'lib/selectors/getMachine';
import { getCanCloseLaborTicket } from 'lib/selectors/getCanOpenLaborTicket';
import { actionOpenLaborTicketModal } from 'lib/actions';
import { LABOR_TICKET_ACTIONS } from 'lib/constants';

import SwipeUpModal from 'components/SwipeUpModal';
import { EmployeeIdPad } from './EmployeeIdPad';
import {
  Root,
  PadRoot,
  PadActions,
  GridWrapper,
  InputWrapper,
  CardGridItem,
  OperatorGrid,
} from './styled';

function stateReducer(state, changes) {
  switch (changes.type) {
    case Downshift.stateChangeTypes.blurInput:
    case Downshift.stateChangeTypes.controlledPropUpdatedSelectedItem:
    case Downshift.stateChangeTypes.mouseUp:
    case Downshift.stateChangeTypes.touchEnd:
    case Downshift.stateChangeTypes.clickItem:
      return state;
    default:
      return changes;
  }
}

const selectedInitialValue = { operator: null, confirmed: false };

/**
 * UserSelect component allows operators to sign in/out and switch between operator profiles
 *
 * @component
 * @param {Object} props
 * @param {Function} props.onSubmit - Callback function called when an operator successfully signs in
 * @param {boolean} props.isOpen - Controls visibility of the modal
 * @param {Function} [props.onClose] - Optional callback function when modal is closed
 */
const UserSelect = ({ onSubmit, isOpen, onClose = () => {} }) => {
  const history = useHistory();
  const { t } = useTranslation();
  const currentOperator = useSelector(getCurrentOperator);

  const operators = useSelector(getActiveOperators);
  const operatorsLoadedAt = useSelector(getOperatorsLoadedAt);
  const [selected, setSelected] = useState(selectedInitialValue);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const unconfirmed =
    !!currentOperator && !!selected.operator && !selected.confirmed;
  const confirmed =
    !!selected.operator && (!currentOperator || selected.confirmed);

  const isLaborTicketEnabled = useSelector(getIsLaborTicketEnabled);

  // Signout flow
  const isJobLaborTrackingEnabled = useSelector(getIsJobLaborTrackingEnabled);
  const laborTicketEnabled = useIsLaborTicketFlow();
  const canCloseLaborTicket = useSelector(getCanCloseLaborTicket);
  const machine = useSelector(getMachine);
  const dispatch = useDispatch();

  const stopOperator = useOperatorLogout(() => {
    onClose();
  });

  const handleSignOut = () => {
    if (laborTicketEnabled && canCloseLaborTicket) {
      history.push(Routes.machineIdHomePath(machine.id));
      return dispatch(
        actionOpenLaborTicketModal(LABOR_TICKET_ACTIONS.OPERATOR_LOGOUT)
      );
    }
    return stopOperator();
  };
  const showSignOut = !isJobLaborTrackingEnabled && !!currentOperator;

  // End Signout flow

  const isPinPadSignIn = useSelector(getPinPadSignInEnabled);

  // Returns an error for the pad to show, or nothing once an operator is
  // selected (the usual confirm/sign-in modals take over from there).
  const handleEmployeeId = (employeeId) => {
    const { status, operator } = findOperatorByEmployeeId(
      operators,
      employeeId
    );
    if (status === EMPLOYEE_ID_MATCH.AMBIGUOUS) {
      return t('More than one operator has this employee ID');
    }
    if (status !== EMPLOYEE_ID_MATCH.FOUND) {
      return t('Employee ID not found');
    }
    if (currentOperator && operator.id === currentOperator.id) {
      return t('This operator is already signed in');
    }
    setSelected({ operator, confirmed: false });
    return undefined;
  };

  const renderPinPad = () => {
    return (
      <PadRoot>
        <EmployeeIdPad
          onSubmit={handleEmployeeId}
          enabled={isOpen && !selected.operator && !isSubmitting}
          isOpen={isOpen}
        />
        {showSignOut && (
          <PadActions>
            <CardGridItem
              color={null}
              type="pill"
              id="employee-id-pad-sign-out"
              onClick={handleSignOut}
            >
              {_startCase(t(`sign out`))}
            </CardGridItem>
          </PadActions>
        )}
      </PadRoot>
    );
  };

  return (
    <Root id="user-select-flow">
      {unconfirmed && isOpen && (
        <SignOutModal
          operator={selected.operator}
          currentOperator={currentOperator}
          onYes={() => {
            return setSelected({ ...selected, confirmed: true });
          }}
          onNo={() => {
            return setSelected(selectedInitialValue);
          }}
          onClose={() => {
            return setSelected(selectedInitialValue);
          }}
        />
      )}
      {confirmed && isOpen && (
        <SignInModalWrapper
          operator={selected.operator}
          isSubmitting={isSubmitting}
          onSubmit={async (...args) => {
            setIsSubmitting(true);
            let result;
            try {
              result = await onSubmit(...args);
            } finally {
              setIsSubmitting(false);
            }
            if (result) {
              setSelected(selectedInitialValue);
            }
          }}
          onClose={() => {
            if (!isSubmitting) setSelected(selectedInitialValue);
          }}
        />
      )}
      <SwipeUpModal
        isOpen={isOpen}
        header={t('Clock in operator')}
        headerStyles={{ fontWeight: '400' }}
        modalStyles={{ background: '#F7FAFC' }}
        detailsStyles={{
          padding: '24px',
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
        }}
        onClose={() => {
          if (!isSubmitting) onClose();
        }}
        details={
          isPinPadSignIn && operators.length > 0 ? (
            renderPinPad()
          ) : (
            <Downshift
              key={operatorsLoadedAt}
              isOpen
              selectedItem={selected.operator}
              stateReducer={stateReducer}
              itemToString={(item) => {
                return item ? item.name : '';
              }}
            >
              {({ getInputProps, getMenuProps, inputValue, getRootProps }) => {
                const filteredOperators = operators.filter((item) => {
                  const isCurrentOperator = !!(
                    currentOperator && item?.id === currentOperator?.id
                  );
                  if (isCurrentOperator) {
                    // exclude active operator from card list
                    return false;
                  }
                  return item.name
                    .toLowerCase()
                    .includes(inputValue.toLowerCase());
                });
                const hasOperatorProfiles = operators.length > 0;

                return (
                  <>
                    <InputWrapper hidden={operators.length === 0}>
                      <SearchInput
                        iconPosition="start"
                        inputStyle={{
                          // Demo search box (fixed px): 40px tall, 4px radius, 1px
                          // Grey400 border, Noto Sans 14px/16px Grey700, 36px left
                          // pad for the icon.
                          height: '40px',
                          borderRadius: '4px',
                          border: '1px solid #CBD5E0',
                          fontFamily: 'inherit',
                          fontSize: '14px',
                          lineHeight: '16px',
                          letterSpacing: '0.07px',
                          color: '#4A5568',
                          paddingLeft: '36px',
                        }}
                        rootProps={getRootProps({}, { suppressRefError: true })}
                        inputProps={getInputProps({
                          placeholder: t('Search by name or reference number'),
                          onBlur: (fn) => {
                            return fn;
                          },
                        })}
                      />
                    </InputWrapper>
                    {hasOperatorProfiles ? (
                      <GridWrapper>
                        <OperatorGrid {...getMenuProps()}>
                          {showSignOut && (
                            <CardGridItem
                              color={null}
                              key="signout"
                              type="pill"
                              onClick={handleSignOut}
                            >
                              {_startCase(t(`sign out`))}
                            </CardGridItem>
                          )}

                          {filteredOperators.map((item) => {
                            return (
                              <CardGridItem
                                color={null}
                                key={item.id}
                                type="pill"
                                onClick={() => {
                                  return setSelected({
                                    operator: item,
                                    confirmed: false,
                                  });
                                }}
                              >
                                {item.name}
                              </CardGridItem>
                            );
                          })}
                        </OperatorGrid>
                      </GridWrapper>
                    ) : (
                      <EmptyWithGraphic
                        icon={hasOperatorProfiles && questionCircle}
                        text={
                          hasOperatorProfiles
                            ? t('No Results Found')
                            : t('No Operators')
                        }
                        subtext={
                          // eslint-disable-next-line no-nested-ternary
                          hasOperatorProfiles
                            ? t('noOperatorFound')
                            : isLaborTicketEnabled
                            ? t('noOperatorResultMES')
                            : t('noOperatorResult')
                        }
                        Graphic={
                          !hasOperatorProfiles && <NoOperators margin="1rem" />
                        }
                      />
                    )}
                  </>
                );
              }}
            </Downshift>
          )
        }
      />
    </Root>
  );
};

UserSelect.propTypes = {
  /** Callback function called when an operator successfully signs in */
  onSubmit: PropTypes.func.isRequired,
  /** Controls visibility of the modal */
  isOpen: PropTypes.bool.isRequired,
  /** Optional callback function when modal is closed */
  onClose: PropTypes.func,
};

export { UserSelect };
